import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createEmpresaFile } from './create-empresa-file';
import { EmpresaFilePayload } from './create-empresa-file.interfaces';

export function downloadEmpresaDetail(empresa: EmpresaFilePayload) {
  const hiddenElement = createHiddenElement(empresa);

  document.body.appendChild(hiddenElement);

  // Esperar a que se rendericen las fuentes y el contenido
  setTimeout(() => {
    html2canvas(hiddenElement, {
      scale: 2, // Mejor calidad
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      width: hiddenElement.scrollWidth,
      height: hiddenElement.scrollHeight,
      onclone: (clonedDoc) => {
        // Asegurar que las fuentes estén cargadas en el documento clonado
        const clonedElement = clonedDoc.querySelector('[data-html2canvas-clone]');
        if (clonedElement) {
          (clonedElement as HTMLElement).style.width = '794px'; // A4 width in pixels at 96 DPI
          (clonedElement as HTMLElement).style.minHeight = '1123px'; // A4 height in pixels at 96 DPI
        }
      }
    }).then((canvas) => {
      const pdf = generatePDF(canvas, empresa);
      pdf.save(`DetalleEmpresa_${empresa.empresa.rut || 'sin_rut'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.removeChild(hiddenElement);
    }).catch((error) => {
      console.error('Error generating PDF:', error);
      document.body.removeChild(hiddenElement);
    });
  }, 500); // Tiempo para que se renderice el contenido
}

/**
 * Crea un elemento oculto con el contenido HTML de la empresa.
 */
function createHiddenElement(empresa: EmpresaFilePayload): HTMLDivElement {
  const hiddenElement = document.createElement('div');
  hiddenElement.style.position = 'fixed';
  hiddenElement.style.top = '-10000px';
  hiddenElement.style.left = '-10000px';
  hiddenElement.style.width = '794px'; // A4 width in pixels at 96 DPI
  hiddenElement.style.minHeight = '1123px'; // A4 height in pixels at 96 DPI
  hiddenElement.style.backgroundColor = '#ffffff';
  hiddenElement.style.fontFamily = 'Arial, sans-serif';
  hiddenElement.style.fontSize = '12px';
  hiddenElement.style.lineHeight = '1.4';
  hiddenElement.setAttribute('data-html2canvas-clone', 'true');
  hiddenElement.innerHTML = createEmpresaFile(empresa);
  return hiddenElement;
}

/**
 * Genera el PDF con el contenido capturado por html2canvas.
 */
function generatePDF(canvas: HTMLCanvasElement, empresa: EmpresaFilePayload): jsPDF {
  const imgData = canvas.toDataURL('image/png', 1.0);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const pdfWidth = 210; // A4 width in mm
  const pdfHeight = 297; // A4 height in mm
  const margin = 10; // 10mm margin for full page
  const contentWidth = pdfWidth - (margin * 2);
  const contentHeight = pdfHeight - (margin * 2);

  // Calcular la altura de la imagen escalada
  const imgAspectRatio = canvas.height / canvas.width;
  const scaledImgHeight = contentWidth * imgAspectRatio;

  const footerText = `www.inase.uy | rgs@inase.uy | ${empresa.userName}`;

  let yPosition = margin;
  let remainingHeight = scaledImgHeight;
  let sourceY = 0;
  let pageCount = 1;

  while (remainingHeight > 0) {
    const availableHeight = contentHeight - (pageCount === 1 ? 0 : 5); // Less space for footer
    const heightToPrint = Math.min(remainingHeight, availableHeight);

    // Calculate the portion of the source image to use
    const sourceHeight = (heightToPrint / contentWidth) * canvas.width;

    // Create a temporary canvas for this page's content
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = sourceHeight;
    const tempCtx = tempCanvas.getContext('2d');

    if (tempCtx) {
      tempCtx.drawImage(
        canvas,
        0, sourceY, canvas.width, sourceHeight,
        0, 0, canvas.width, sourceHeight
      );

      const tempImgData = tempCanvas.toDataURL('image/png', 1.0);
      pdf.addImage(tempImgData, 'PNG', margin, yPosition, contentWidth, heightToPrint);
    }

    remainingHeight -= heightToPrint;
    sourceY += sourceHeight;

    // Add footer on last page
    if (remainingHeight <= 0) {
      addFooter(pdf, footerText);
    }

    if (remainingHeight > 0) {
      pdf.addPage();
      yPosition = margin;
      pageCount++;
    }
  }

  return pdf;
}

/**
 * Agrega un pie de página solo en la última página.
 */
function addFooter(pdf: jsPDF, footerText: string) {
  const marginBottom = 10;
  const pageWidth = 210;
  const pageHeight = 297;

  pdf.setFontSize(9);
  pdf.setTextColor(51, 51, 102); // Color azul oscuro
  pdf.text(footerText, pageWidth / 2, pageHeight - marginBottom, { align: 'center' });
}
